import * as React from "react";

import { Button } from "src/components/buttons";
import { DataTable } from "src/components/common";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";

import PickListContext from "src/context/pickListContext";

import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";
import {
	setStatusCellValue,
	StatusFilterMethod,
	filterByStatus,
} from "src/components/forms/StatusFilter";
import {
	ClearFilterButton,
	ActionIcon,
} from "src/components/forms/ActionColumnIconsAndButton";
import { AdminPermissions } from "src/common/Permissions";
import { Tooltip } from "antd";

interface IProps {
	history: any;
	actions: any;
	match: any;
	location?: any;
}

interface IState {
	organization: OrganizationDTO;
	locations: LocationDTO[];
	filtered: any[];
	loading: boolean;
}

class OrganizationLocations extends React.Component<IProps, IState> {
	public static contextType = PickListContext;
	public organizationId = this.props.match.params.organizationId;
	public _isMounted = true;
	constructor(props: IProps) {
		super(props);
		this.state = {
			organization: {},
			locations: [],
			filtered: [],
			loading: false,
		};
	}

	public componentDidMount() {
		const { organizationId } = this.props.match.params;
		if (organizationId) {
			this.setState({ loading: true });
			OrganizationAPI.getOrganization({ id: organizationId })
				.then((organization) => {
					if (this._isMounted) {
						this.setState({ organization });
					}
				})
				.then(() => LocationAPI.getOrganizationLocations(organizationId))
				.then((locations: LocationDTO[]) => {
					if (this._isMounted) {
						this.setState({ locations, loading: false });
					}
				});
			this.setState({ filtered: [{ id: "isActive", value: "Active" }] });
		}
	}

	public componentWillUnmount() {
		this._isMounted = false;
	}

	public render() {
		const { locations } = this.state;
		return (
			<div className="container-fluid">
				<AuthorizationWrapper
					type="hide"
					permissions={[AdminPermissions.EditOrganizationLocation]}
				>
					<div className="d-flex mb-4">
						<div className="ml-3">
							<p className="text-danger font-weight-bold">
								IMPORTANT: If this is part of a multi-org configuration, please
								update the other organization(s) accordingly!
							</p>
						</div>
						<div className="ml-auto">
							<Button
								className="btn-primary"
								to={{
									pathname: this.getActionLink(this.organizationId, -1),
								}}
							>
								New Location
							</Button>
						</div>
					</div>
				</AuthorizationWrapper>
				<DataTable
					data={locations}
					columns={this.getColumns()}
					filterable={true}
					filtered={this.state.filtered}
					onFilteredChange={(filtered: any) => this.setState({ filtered })}
					onRowClick={this.onRowClick}
					loading={this.state.loading}
				/>
			</div>
		);
	}

	private getActionLink(orgId: any, rowId: any): string {
		return `/admin/organizations/${orgId}/locations/${rowId}`;
	}

	private onRowClick = ({ original }: { original?: any }) => {
		this.props.history.push(
			this.getActionLink(this.organizationId, original.id)
		);
	};

	private getColumns = () => {
		const columns = [
			{
				Header: <Tooltip title="Location">Location</Tooltip>,
				accessor: "name",
				filterable: true,
			},
			{
				Header: <Tooltip title="Active">Active</Tooltip>,
				accessor: "isActive",
				filterMethod: filterByStatus("isActive"),
				Filter: StatusFilterMethod,
				Cell: setStatusCellValue,
			},
			{
				Header: <Tooltip title="Address">Address</Tooltip>,
				accessor: "address1",
				filterable: true,
			},
			{
				Header: <Tooltip title="City">City</Tooltip>,
				accessor: "city",
				filterable: true,
			},
			{
				Header: <Tooltip title="Province/State">Province/State</Tooltip>,
				accessor: "provinceId",
				filterable: true,
				filterMethod: this.picklistDescriptionFilter,
				Cell: this.picklistDescriptionCell("provinceId"),
			},
			{
				Header: <Tooltip title="Country">Country</Tooltip>,
				accessor: "countryId",
				filterable: true,
				filterMethod: this.picklistDescriptionFilter,
				Cell: this.picklistDescriptionCell("countryId"),
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				id: "Actions",
				Cell: <ActionIcon title="Details" type="open" />,
				Filter: (
					<ClearFilterButton
						onClick={() =>
							this.setState({ filtered: [{ id: "isActive", value: "Active" }] })
						}
					/>
				),
			},
		];

		return columns;
	};

	private picklistDescriptionCell = (prop: string) => (rowInfo: any) => {
		if (rowInfo.row[prop] === undefined) {
			return "N/A";
		} else {
			if (this.context.pickListItems && this.context.pickListItems.length > 0) {
				const foundItem = this.context.pickListItems.find(
					(item: PickListItemResponseDTO) => item.id === rowInfo.row[prop]
				) as PickListItemResponseDTO;
				if (foundItem) {
					return foundItem.description!;
				} else {
					return "N/A";
				}
			} else {
				return "Loading";
			}
		}
	};

	private picklistDescriptionFilter = (filter: any, row: LocationDTO) => {
		if (row[filter.id] === undefined) {
			return false;
		} else {
			const picklistItemDescription = this.context.pickListItems.find(
				(item: PickListItemResponseDTO) => item.id === row[filter.id]
			)!.description!;
			return (
				picklistItemDescription
					.toLowerCase()
					.indexOf(filter.value.toLowerCase()) >= 0
			);
		}
	};
}

export default OrganizationLocations;
